import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import * as PropTypes from 'prop-types';
import {
  Modal, Table, Typography, Form, Input, Row, Button,
} from 'antd';
import { FormattedMessage, injectIntl } from 'react-intl';
import { cancelEditVehicleDamage, requestVehicleDamages, requestUpdateVehicleDamages } from '../../../../state/ui/configuration/vehicles/vehicleUiActions';
import {
  selectEditState, selectVehicleDamagesRequest, selectSubmitVehicleDamagesRequest,
} from '../../../../state/ui/configuration/vehicles/vehicleUiReducer';
import { selectVehicle } from '../../../../state/domain/vehicles/vehicleReducer';


const { Column } = Table;
const { Text } = Typography;

// SORTS
function sortDamageByDesc(a, b) {
  const aStr = `${a.damageArea.damageAreaDescription}`;
  const bStr = `${b.damageArea.damageAreaDescription}`;
  return aStr.localeCompare(bStr);
}

/**
 * The field for the registration of the vehicle label. Allows clear, and max length set. Required plus extra duplicate validation
 * The rules are injected into the form using the form field decorator.
 */
const RegNoField = injectIntl(({
  form, vehicle, intl,
}) => (
  <Form.Item label={<FormattedMessage id="regNumber" />}>
    {
      form.getFieldDecorator('regNumber', {
        initialValue: vehicle ? vehicle.regNumber : undefined,
        rules: [
          { required: false, message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
        <Input
          type="text"
          disabled
          placeholder={intl.formatMessage({ id: 'enter' })}
        />,
      )
    }
  </Form.Item>
));

// TABLE
function VehicleDamagesTable({
  vehicleDamages, isLoading, rowSelection,
}) {
  const RowTotal = () => (
    <div>
      <Text>{`${vehicleDamages.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  // TABLE SETTINGS AND COLUMNS
  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      loading={isLoading}
      dataSource={vehicleDamages}
      rowKey={vehicleDamage => vehicleDamage.damageId}
      rowSelection={rowSelection}
    >
      <Column
        title={<FormattedMessage id="damageArea" />}
        dataIndex="damageArea.damageAreaDescription"
        key="damageArea"
        sorter={sortDamageByDesc}
        defaultSortOrder="ascend"
      />
      <Column
        title={<FormattedMessage id="damageType" />}
        dataIndex="damageType.damageTypeDescription"
        key="damageType"
      />
    </Table>
  );
}

VehicleDamagesTable.propTypes = {
  isLoading: PropTypes.bool,
  vehicleDamages: PropTypes.arrayOf(
    PropTypes.shape({
      damageAreaDescription: PropTypes.string,
      damageTypeDescription: PropTypes.string,
    }).isRequired,
  ),
  rowSelection: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
  }).isRequired,
};

VehicleDamagesTable.defaultProps = {
  vehicleDamages: [],
  isLoading: false,
};


/**
 * A component allowing a bynx admin user to mark damages against a vehicle as being repaired
 */
const EditVehicleDamage = Form.create({ name: 'edit-vehicle-damage' })(({
  visible, onCancel, form, vehicle, loadVehicleDamages, onUpdate, isSubmitting, vehicleDamagesIsLoading,
}) => {
  const vehicleId = vehicle ? vehicle.vehicleId : undefined;
  const { resetFields } = form;
  const vehicleDamages = vehicle ? vehicle.damageItems : [];
  const [selectedRowKeys, onSelectChange] = useState([]);

  const rowSelection = {
    // set the state for each row selection or deselection
    onChange: (selectedRows) => { onSelectChange(selectedRows); },
  };

  const submitVehicleRepairs = () => {
    form.validateFields((err) => {
      if (err) return; // Nothing more to do.

      onUpdate(vehicle, selectedRowKeys);
    });
  };

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) {
      resetFields();
      loadVehicleDamages(vehicleId);
    }
  }, [resetFields, visible, loadVehicleDamages, vehicleId]);

  return (
    <Modal
      destroyOnClose
      title={<FormattedMessage id="repairDamage" />}
      visible={visible}
      onOk={submitVehicleRepairs}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      footer={
        <Row type="flex" align="middle" justify="end">
          <div>
            <Button onClick={onCancel}>
              <FormattedMessage id="cancel" />
            </Button>
            <Button key="submit" type="primary" loading={vehicleDamagesIsLoading || isSubmitting} onClick={submitVehicleRepairs}>
              <FormattedMessage id="repair" />
            </Button>
          </div>
        </Row>
      }
    >
      <Form layout="vertical">
        <RegNoField form={form} vehicle={vehicle} />
        <VehicleDamagesTable form={form} vehicleDamages={vehicleDamages} rowSelection={rowSelection} isLoading={vehicleDamagesIsLoading} />
      </Form>
    </Modal>
  );
});

const mapStateToProps = (state) => {
  const editVehicleDamageState = selectEditState(state);
  const submitRequest = selectSubmitVehicleDamagesRequest(state);
  const vehicleDamagesRequest = selectVehicleDamagesRequest(state);

  return {
    visible: editVehicleDamageState.isEditingVehicleDamage,
    vehicle: editVehicleDamageState.vehicleId ? selectVehicle(state, editVehicleDamageState.vehicleId) : null,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    validationBox: submitRequest ? submitRequest.error : undefined,
    vehicleDamagesIsLoading: vehicleDamagesRequest ? vehicleDamagesRequest.isLoading : false,
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdate: (vehicle, repairedDamageItems) => dispatch(requestUpdateVehicleDamages(vehicle, repairedDamageItems)),
  loadVehicleDamages: vehicleId => dispatch(requestVehicleDamages(vehicleId)),
  onCancel: () => dispatch(cancelEditVehicleDamage()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditVehicleDamage);
